import { Link } from "react-router-dom";
import { useSettings } from "@/lib/SettingsContext";
import usePageMeta from "@/lib/usePageMeta";
import renderRich from "@/lib/renderRich";
import { ArrowRight, Leaf, HeartHandshake, Sparkles } from "lucide-react";

const values = [
  {
    icon: <Leaf size={18} />,
    title: "Dr Hauschka Skincare",
    text: "Plant-based, certified-natural products that nourish & soothe.",
  },
  {
    icon: <HeartHandshake size={18} />,
    title: "Personal & Caring",
    text: "Every treatment is tailored to how you feel that very day.",
  },
  {
    icon: <Sparkles size={18} />,
    title: "Trained in Holistic",
    text: "Hot stones, Lomi Lomi, Thai compress, Indian head & pregnancy.",
  },
];

export default function About() {
  usePageMeta(
    "About Deborah Louise · Beautify — Wadebridge, Cornwall",
    "Meet Deborah Louise, qualified beauty & massage therapist in Wadebridge, Cornwall. Nourishing Dr Hauschka products, calm studios and treatments tailored to you."
  );
  const { images: IMAGES, content: CONTENT } = useSettings();
  const ABOUT = (CONTENT && CONTENT.about) || {};
  return (
    <div data-testid="about-page" className="bg-grain">
      <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-10 py-10 sm:py-16">
        <header className="text-center mb-12 sm:mb-16">
          <span className="text-xs tracking-[0.3em] uppercase text-plum font-semibold">
            About
          </span>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl text-plum-deep mt-4">
            Deborah Louise
          </h1>
        </header>

        <section className="grid lg:grid-cols-12 gap-8 sm:gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="rounded-[2rem] overflow-hidden shadow-xl border border-white">
              <img
                src={IMAGES.deborah}
                alt="Deborah Louise"
                className="w-full object-cover"
                data-testid="about-deborah-image"
              />
            </div>
          </div>
          <div className="lg:col-span-7">
            <p className="font-display italic text-2xl sm:text-3xl text-plum leading-tight">
              {ABOUT.lead}
            </p>
            <div data-testid="about-bio" className="mt-6 space-y-5 text-foreground/75 leading-relaxed">
              {(ABOUT.paragraphs || []).map((p, i) => (
                <p key={i}>{renderRich(p)}</p>
              ))}
              <p className="font-display italic text-lg sm:text-xl text-plum-deep pt-1">
                {ABOUT.signoff}
              </p>
            </div>

            <div className="mt-10 grid sm:grid-cols-3 gap-4">
              {values.map((v) => (
                <div
                  key={v.title}
                  data-testid={`about-value-${v.title.toLowerCase().replace(/\s+/g, "-")}`}
                  className="bg-white rounded-2xl p-5 border border-blush-deep/40"
                >
                  <div className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-secondary text-plum">
                    {v.icon}
                  </div>
                  <div className="mt-3 font-semibold text-plum">{v.title}</div>
                  <div className="text-sm text-foreground/65 mt-1">{v.text}</div>
                </div>
              ))}
            </div>

            <div className="mt-10">
              <Link
                to="/contact"
                data-testid="about-book-cta"
                className="inline-flex items-center gap-2 rounded-full bg-plum text-white px-7 py-3.5 text-sm font-medium hover:bg-plum-light transition-all hover:-translate-y-0.5"
              >
                Book with Deborah <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </section>

        {/* gallery strip */}
        <section className="mt-16 sm:mt-24">
          <span className="text-xs tracking-[0.3em] uppercase text-plum font-semibold">
            Inside the studio
          </span>
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl text-plum-deep mt-3 mb-8 sm:mb-10">
            A space to slow down
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
            <img src={IMAGES.workspace} alt="Treatment room" loading="lazy" decoding="async" className="rounded-2xl object-cover h-40 sm:h-56 md:h-64 w-full" />
            <img src={IMAGES.storefront} alt="Our new sign outside Elixir Health, Molesworth Street" data-testid="about-storefront-image" loading="lazy" decoding="async" className="rounded-2xl object-cover h-40 sm:h-56 md:h-64 w-full" />
            <img src={IMAGES.lash1} alt="Lash lift" loading="lazy" decoding="async" className="rounded-2xl object-cover h-40 sm:h-56 md:h-64 w-full" />
            <img src={IMAGES.lash2} alt="Lash lift result" loading="lazy" decoding="async" className="rounded-2xl object-cover h-40 sm:h-56 md:h-64 w-full" />
          </div>
        </section>
      </div>
    </div>
  );
}
